import { zodResolver } from "@hookform/resolvers/zod";
import {
  useCreateClient,
  useCreateInvestmentAccount,
  useDistributorProfile,
} from "creso-core/hooks";
import { CreateClientSchema } from "creso-core/zod";
import {
  DialogFooter,
  Form,
  FormField,
  FormInput,
  FormSelect,
  Button,
  Popover,
  PopoverAnchor,
  PopoverArrow,
  PopoverContent,
  cn,
} from "web-design-system";
import { CopyIcon, InfoIcon, Loader2, Loader2Icon } from "lucide-react";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { handleError, handleFormValidationErrors } from "@/utils/error";
import { useAuthContext } from "@/providers/auth-provider";
import { getInitials, isEmployeeUser } from "creso-core/utils";
import { useCopyToClipboard, useLocalStorage } from "usehooks-ts";
import { ConditionalRenderer } from "@core/components/conditional-renderer";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { Avatar, ClientStatusBadge, FormCountryCodePhoneInput } from "web-core";
import { Client } from "creso-core/types";
import { ACCOUNT_TYPE_OPTIONS } from "creso-core/constants";
import { useCheckKyc } from "@crm/hooks/onboarding";
import { KycDescription, KycStatusBadge } from "./kyc-status";
import { MinorKycFields } from "./minor-kyc-fields";

export function ClientLeadAccountForm(props: { onClose?: () => void }) {
  const { onClose } = props;
  const { user, employee } = useAuthContext();
  const { data: profile } = useDistributorProfile();
  const isEmployee = isEmployeeUser(user.user_type);
  const onboardingUrl = `${window.location.origin}/mfd/${profile?.handle}${isEmployee && employee?.id ? `?employee_id=${employee?.id}` : ""}`;

  const [, copy] = useCopyToClipboard();
  const [accountTypeHintSeen, setAccountTypeHintSeen] = useLocalStorage(
    "account-type-hint-seen",
    false
  );
  const [client, setClient] = useState<Client | null>(null);
  const [cachedClientName, setCachedClientName] = useState<string | null>(null);

  const [checkKyc, isCheckingKyc] = useCheckKyc(client?.id ?? 0);

  const form = useForm({
    resolver: zodResolver(CreateClientSchema),
    values: {
      holding_pattern: "single",
      tax_status: client?.tax_status || "resident_individual",
      pan: client?.pan || "",
      phone_number: client?.phone_number || "",
      name: client?.name || "",
      email: client?.email,
    },
  });

  const { isSubmitting, dirtyFields } = form.formState;
  const panIsDirty = !!dirtyFields.pan;
  const panValue = form.watch("pan");
  const taxStatus = form.watch("tax_status");
  const isNRI = taxStatus === "nri";
  const isMinor = taxStatus === "on_behalf_of_minor";

  const createClient = useCreateClient();
  const navigate = useNavigate();
  const createInvestmentAccount = useCreateInvestmentAccount();

  const [isMinorLead, setIsMinorLead] = useState(false);
  const [isGuardianVerified, setIsGuardianVerified] = useState(false);

  const addEditClient = async (data: z.infer<typeof CreateClientSchema>) => {
    try {
      const client = await createClient.mutateAsync(data);
      setClient(client);
      setIsMinorLead(data.tax_status === "on_behalf_of_minor");
    } catch (error) {
      handleFormValidationErrors(form, error);
      handleError(error, "Failed to create client");
    }
  };

  const handleCheckKyc = useCallback(
    async (data: z.infer<typeof CreateClientSchema>) => {
      setCachedClientName(client?.name || null);
      try {
        const client = await checkKyc(data);
        setClient(client);
      } catch (e) {
        handleError(e, "Failed to check KYC status");
      }
    },
    [client, checkKyc]
  );

  const createAccount = useCallback(async () => {
    if (!client?.id) return;
    try {
      await createInvestmentAccount.mutateAsync(client.id);
      toast.success("Client account created");
      onClose?.();
      navigate({
        to: "/clients/$id/portfolio",
        params: {
          id: client.id.toString(),
        },
        search: {},
      });
    } catch (error) {
      handleError(error, "Failed to send consent link");
    }
  }, [client, createInvestmentAccount, navigate, onClose]);

  const clientCreated = !!client;
  const showCheckKycButton =
    !client?.kyc_status ||
    client?.kyc_status === "U" ||
    !panValue ||
    panIsDirty;
  const showKycStatus = !showCheckKycButton;
  const nameChanged =
    showKycStatus &&
    !!cachedClientName &&
    !!client?.name &&
    cachedClientName !== client.name;

  return (
    <div className="flex flex-col size-full p-6">
      <span className="text-2xl font-semibold mb-6">
        {clientCreated ? "Create  Account" : "New Client"}
      </span>
      <Form {...form}>
        <form className="flex flex-col flex-1 gap-6">
          <ConditionalRenderer condition={!clientCreated}>
            <Popover open={!accountTypeHintSeen}>
              <PopoverAnchor asChild>
                <div>
                  <FormField
                    control={form.control}
                    name="tax_status"
                    render={({ field }) => (
                      <FormSelect
                        {...field}
                        label="Account type"
                        options={ACCOUNT_TYPE_OPTIONS}
                        onChange={(value) => {
                          field.onChange(value);
                          setAccountTypeHintSeen(true);
                          if (
                            value !== "nri" &&
                            !form.getValues("phone_number").startsWith("+91")
                          ) {
                            form.setValue("phone_number", "+91");
                          }
                        }}
                      />
                    )}
                  />
                </div>
              </PopoverAnchor>
              <PopoverContent
                side="bottom"
                align="start"
                onOpenAutoFocus={(e) => e.preventDefault()}
                className="flex flex-col gap-3 border-none bg-foreground text-background"
              >
                <span className="text-sm font-semibold">
                  New: add NRI and Minor clients here
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="self-end text-foreground"
                  onClick={() => setAccountTypeHintSeen(true)}
                >
                  Got it
                </Button>
                <PopoverArrow className="fill-foreground" />
              </PopoverContent>
            </Popover>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormInput
                  {...field}
                  label={isMinor ? "Minor's name" : "Name"}
                  placeholder="Name"
                  value={field.value || ""}
                />
              )}
            />
            <FormField
              control={form.control}
              name="phone_number"
              render={({ field }) => (
                <FormCountryCodePhoneInput
                  {...field}
                  allowCountryChange={isNRI}
                  // A minor's account uses the guardian's mobile and email
                  label={isMinor ? "Guardian's mobile" : "Mobile"}
                  value={field.value || "+91"}
                  placeholder="9999999999"
                />
              )}
            />
            <FormField
              name="email"
              render={({ field }) => (
                <FormInput
                  label={
                    <>
                      <span>{isMinor ? "Guardian's email" : "Email"}</span>
                      &nbsp;
                      <span className="text-muted-foreground">(Optional)</span>
                    </>
                  }
                  {...field}
                  placeholder="Enter email address"
                />
              )}
            />
          </ConditionalRenderer>
          <ConditionalRenderer condition={clientCreated}>
            <div className="rounded-lg p-3 border border-border flex items-center gap-2">
              <Avatar fallback={getInitials(client?.name || "")} size="sm" />
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-sm font-semibold">{client?.name}</span>
                {client?.phone_number && (
                  <span className="text-xs text-muted-foreground">
                    {client.phone_number}
                  </span>
                )}
              </div>
              <ClientStatusBadge status={client?.status ?? "LEAD"} />
            </div>
            {isMinorLead ? (
              <MinorKycFields
                client={client}
                onVerifiedChange={setIsGuardianVerified}
                disabled={isSubmitting}
              />
            ) : (
              <div className="flex flex-col gap-3">
                <div className="flex flex-row items-start justify-between gap-2">
                  <FormField
                    control={form.control}
                    name="pan"
                    className="flex-2"
                    render={({ field }) => (
                      <FormInput
                        {...field}
                        disabled={isSubmitting}
                        label="PAN"
                        placeholder="ABCDE1234J"
                        onChange={(val) =>
                          field.onChange(val?.toLocaleUpperCase() || "")
                        }
                      />
                    )}
                  />
                  <div className="shrink-0 pt-5">
                    {showCheckKycButton ? (
                      <Button
                        variant="outline"
                        disabled={isCheckingKyc || !panValue}
                        type="button"
                        onClick={form.handleSubmit(handleCheckKyc)}
                      >
                        <Loader2Icon
                          className={cn("size-4 animate-spin", {
                            hidden: !isCheckingKyc,
                          })}
                        />
                        Check KYC
                      </Button>
                    ) : (
                      <KycStatusBadge
                        kyc_status={client?.kyc_status}
                        remark={client?.kyc_status_remark}
                      />
                    )}
                  </div>
                </div>
                {showKycStatus && (
                  <KycDescription
                    client={client}
                    cachedName={nameChanged ? cachedClientName : undefined}
                    onCheckStatus={form.handleSubmit(handleCheckKyc)}
                    isCheckingStatus={isCheckingKyc}
                  />
                )}
              </div>
            )}
          </ConditionalRenderer>
          <DialogFooter className="flex sm:flex-col gap-2 mt-auto">
            <ConditionalRenderer condition={clientCreated}>
              {(isMinorLead ? !isGuardianVerified : showCheckKycButton) && (
                <div className="flex items-center gap-2 rounded-lg bg-tag-slate p-3 border border-border w-full">
                  <InfoIcon className="size-4 shrink-0 text-tag-slate-foreground" />
                  <span className="text-xs text-tag-slate-foreground">
                    Complete KYC check to create the account
                  </span>
                </div>
              )}
              <Button
                className="w-full"
                disabled={
                  createInvestmentAccount.isPending ||
                  isSubmitting ||
                  (isMinorLead ? !isGuardianVerified : showCheckKycButton)
                }
                type="button"
                onClick={
                  isMinorLead ? createAccount : form.handleSubmit(createAccount)
                }
              >
                Create Account
              </Button>
              <Button
                type="button"
                className="w-full"
                onClick={() => {
                  setClient(null);
                  setCachedClientName(null);
                  setIsMinorLead(false);
                  setIsGuardianVerified(false);
                }}
                variant="ghost"
              >
                Add Another Lead
              </Button>
            </ConditionalRenderer>
            <ConditionalRenderer condition={!clientCreated}>
              <Button
                className="w-full"
                type="button"
                onClick={form.handleSubmit(addEditClient)}
                disabled={createClient.isPending}
              >
                {createClient.isPending && (
                  <Loader2 className="ml-2 size-4 animate-spin" />
                )}
                Add
              </Button>
              <Button
                type="button"
                className="w-full"
                onClick={() => {
                  copy(onboardingUrl);
                  toast.info("Onboarding link copied to clipboard");
                }}
                variant="ghost"
              >
                Or share onboarding link <CopyIcon />{" "}
              </Button>
            </ConditionalRenderer>
          </DialogFooter>
        </form>
      </Form>
    </div>
  );
}
